import React, { useCallback, useEffect, useState } from 'react'
import { getLocalApiUrl, authFetch } from '../../../config/api'
import toast from 'react-hot-toast'
import {
  ArrowPathIcon,
  CheckCircleIcon,
  CircleStackIcon,
  ExclamationTriangleIcon,
  ServerIcon,
} from '@heroicons/react/24/outline'
import { DbStatusBadge } from './tenantShared'

const TIERS = ['standard', 'professional', 'enterprise']
const STATUSES = ['active', 'inactive', 'suspended']

function MigrationStatusPanel({ groupId, hasDatabase, onTenantUpdated }) {
  const [loading, setLoading] = useState(false)
  const [status, setStatus] = useState(null)
  const [showAll, setShowAll] = useState(false)

  const load = useCallback(async () => {
    if (!groupId || !hasDatabase) {
      setStatus(null)
      return
    }
    setLoading(true)
    try {
      const res = await authFetch(getLocalApiUrl(`/api/admin/tenants/${groupId}/database/migrations`))
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
      setStatus(data)
    } catch (e) {
      toast.error(e.message || 'Failed to load migration status')
      setStatus(null)
    } finally {
      setLoading(false)
    }
  }, [groupId, hasDatabase])

  useEffect(() => {
    load()
  }, [load])

  if (!hasDatabase) return null

  const pending = status?.migrations?.filter((m) => m.status === 'pending') || []
  const visible = showAll ? status?.migrations || [] : pending

  return (
    <div className="space-y-3 pt-2 border-t border-gray-100">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          {loading && !status ? (
            <span className="text-xs text-gray-500">Loading migrations…</span>
          ) : status?.upToDate ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-800">
              <CheckCircleIcon className="h-3.5 w-3.5" />
              All {status.totalCount} migrations applied
            </span>
          ) : status ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800">
              <ExclamationTriangleIcon className="h-3.5 w-3.5" />
              {status.pendingCount} pending · schema v{status.currentVersion} of v{status.latestNumber}
            </span>
          ) : null}
          {status && (
            <span className="text-xs text-gray-400">
              {status.appliedCount}/{status.totalCount} applied
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {status && status.totalCount > 0 && (
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              className="text-xs text-psg-teal hover:underline"
            >
              {showAll
                ? status.pendingCount > 0
                  ? 'Show pending only'
                  : 'Hide list'
                : 'Show all migrations'}
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              load()
              onTenantUpdated?.()
            }}
            disabled={loading}
            className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-50"
          >
            <ArrowPathIcon className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      <p className="text-xs text-gray-500">
        Migrations are applied on the server during deploy (
        <code className="rounded bg-gray-100 px-1 font-mono">migrate_tenant_dbs.js</code>
        ). This view is read-only.
      </p>

      {status && visible.length > 0 && (
        <div className="max-h-64 overflow-y-auto rounded-md border">
          <table className="min-w-full text-xs">
            <thead className="sticky top-0 bg-gray-50 text-left text-gray-500">
              <tr>
                <th className="px-3 py-2 font-medium w-16">#</th>
                <th className="px-3 py-2 font-medium">File</th>
                <th className="px-3 py-2 font-medium w-24">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {visible.map((m) => (
                <tr key={m.file} className={m.status === 'pending' ? 'bg-amber-50/50' : ''}>
                  <td className="px-3 py-1.5 font-mono tabular-nums text-gray-600">{m.number}</td>
                  <td className="px-3 py-1.5 font-mono text-gray-800">{m.file}</td>
                  <td className="px-3 py-1.5">
                    {m.status === 'applied' ? (
                      <span className="text-emerald-700">Applied</span>
                    ) : (
                      <span className="font-medium text-amber-800">Pending</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {status && !showAll && pending.length === 0 && status.upToDate && status.totalCount > 0 && (
        <p className="text-xs text-gray-500">No pending migrations. Use “Show all migrations” to browse the full list.</p>
      )}
    </div>
  )
}

export default function TenantOverviewTab({ tenant, onTenantUpdated }) {
  const groupId = tenant?.group_id
  const [action, setAction] = useState(null)
  const [details, setDetails] = useState({
    name: '',
    subdomain: '',
    tier: 'standard',
    status: 'active',
  })
  const [savingDetails, setSavingDetails] = useState(false)

  useEffect(() => {
    if (!tenant) return
    setDetails({
      name: tenant.name || '',
      subdomain: tenant.subdomain || tenant.group_id || '',
      tier: tenant.tier || 'standard',
      status: tenant.status || 'active',
    })
  }, [tenant])

  const createTenantDb = async () => {
    setAction('createDb')
    try {
      const res = await authFetch(getLocalApiUrl(`/api/admin/tenants/${groupId}/database`), { method: 'POST' })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
      toast.success(`Database "${data.dbName}" created`)
      onTenantUpdated?.()
    } catch (e) {
      toast.error(e.message || 'Create failed')
    } finally {
      setAction(null)
    }
  }

  const saveTenantDetails = async (e) => {
    e.preventDefault()
    const name = details.name.trim()
    const subdomain = details.subdomain.trim().toLowerCase()
    if (!name) {
      toast.error('Display name is required')
      return
    }
    if (!/^[a-z0-9]{2,16}$/.test(subdomain)) {
      toast.error('Subdomain must be 2–16 lowercase letters or numbers')
      return
    }
    setSavingDetails(true)
    try {
      const res = await authFetch(getLocalApiUrl(`/api/admin/tenants/${encodeURIComponent(groupId)}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          subdomain,
          tier: details.tier,
          status: details.status,
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
      toast.success('Tenant details saved')
      onTenantUpdated?.()
    } catch (err) {
      toast.error(err.message || 'Save failed')
    } finally {
      setSavingDetails(false)
    }
  }

  const healthCheckTenantDb = async () => {
    setAction('health')
    try {
      const res = await authFetch(getLocalApiUrl(`/api/admin/tenants/${groupId}/database/health`))
      const data = await res.json().catch(() => ({}))
      if (!res.ok || data.status !== 'healthy') throw new Error(data.error || 'unhealthy')
      toast.success('Database is healthy')
    } catch (e) {
      toast.error(e.message || 'Health check failed')
    } finally {
      setAction(null)
    }
  }

  if (!tenant) return null

  return (
    <div className="space-y-6 max-w-3xl">
      <form
        onSubmit={saveTenantDetails}
        className="rounded-lg border bg-white p-5 space-y-4"
      >
        <h3 className="text-sm font-semibold text-gray-900">Tenant details</h3>
        <p className="text-xs text-gray-500">
          GSID <span className="font-mono text-gray-700">{groupId}</span> cannot be changed after provision.
          Customer URL: <span className="font-mono">{details.subdomain || groupId}.paysafego.net</span>
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block text-sm sm:col-span-2">
            <span className="font-medium text-gray-700">Display name</span>
            <input
              required
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              value={details.name}
              onChange={(e) => setDetails((d) => ({ ...d, name: e.target.value }))}
            />
          </label>
          <label className="block text-sm">
            <span className="font-medium text-gray-700">Subdomain</span>
            <input
              required
              pattern="[a-z0-9]{2,16}"
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono"
              value={details.subdomain}
              onChange={(e) =>
                setDetails((d) => ({
                  ...d,
                  subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ''),
                }))
              }
            />
          </label>
          <label className="block text-sm">
            <span className="font-medium text-gray-700">Tier</span>
            <select
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm capitalize"
              value={details.tier}
              onChange={(e) => setDetails((d) => ({ ...d, tier: e.target.value }))}
            >
              {TIERS.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            <span className="font-medium text-gray-700">Status</span>
            <select
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm capitalize"
              value={details.status}
              onChange={(e) => setDetails((d) => ({ ...d, status: e.target.value }))}
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="flex justify-end pt-1">
          <button
            type="submit"
            disabled={savingDetails}
            className="rounded-lg bg-psg-teal px-4 py-2 text-sm font-medium text-white hover:opacity-95 disabled:opacity-50"
          >
            {savingDetails ? 'Saving…' : 'Save tenant details'}
          </button>
        </div>
      </form>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="rounded-lg border bg-white p-4">
          <p className="text-xs text-gray-500">Tier</p>
          <p className="text-sm font-medium capitalize">{tenant.tier || '—'}</p>
        </div>
        <div className="rounded-lg border bg-white p-4">
          <p className="text-xs text-gray-500">Status</p>
          <p className="text-sm font-medium capitalize">{tenant.status || '—'}</p>
        </div>
        <div className="rounded-lg border bg-white p-4">
          <p className="text-xs text-gray-500">Venues</p>
          <p className="text-sm font-medium tabular-nums">{tenant.venue_count ?? '—'}</p>
        </div>
        <div className="rounded-lg border bg-white p-4">
          <p className="text-xs text-gray-500">Users</p>
          <p className="text-sm font-medium tabular-nums">{tenant.user_count ?? '—'}</p>
        </div>
      </div>

      <div className="rounded-lg border bg-white p-5 space-y-3">
        <h3 className="text-sm font-semibold text-gray-900">Database</h3>
        <div className="flex flex-wrap items-center gap-2">
          <DbStatusBadge tenant={tenant} />
          {tenant.db_name && (
            <span className="text-xs font-mono text-gray-500">{tenant.db_name}</span>
          )}
          {tenant.db_schema_version > 0 && (
            <span className="text-xs text-gray-400">schema v{tenant.db_schema_version}</span>
          )}
        </div>
        <div className="flex flex-wrap gap-2 pt-2">
          {!tenant.db_name && (
            <button
              type="button"
              onClick={createTenantDb}
              disabled={!!action}
              className="inline-flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-sm text-emerald-800 hover:bg-emerald-100 disabled:opacity-50"
            >
              {action === 'createDb' ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <CircleStackIcon className="h-4 w-4" />}
              Create dedicated DB
            </button>
          )}
          {tenant.db_name && tenant.db_status === 'ready' && (
            <button
              type="button"
              onClick={healthCheckTenantDb}
              disabled={!!action}
              className="inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {action === 'health' ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <ServerIcon className="h-4 w-4" />}
              Health check
            </button>
          )}
        </div>

        <MigrationStatusPanel
          groupId={groupId}
          hasDatabase={!!tenant.db_name}
          onTenantUpdated={onTenantUpdated}
        />
      </div>
    </div>
  )
}
